/**
 * 账号详情 /accounts/:id：身份与状态、钱包与策略、Key（含系统 Key）、花瓣流水、请求明细、订单、审计。
 *
 * 背景：2026-11-03 取消组织，Key、钱包、用量、订单直接挂账号；原「组织详情」的能力并进这一页，
 *   客服在这里处理「为什么扣了这么多」「补偿一下」「把这个 Key 停了」，管理员给服务账号（如 Yeou 游戏服务端）建 Key。
 *   账号分 kind：user（有登录入口）与 service（没有登录入口，只在这里管理）。
 * 设计意图：
 *   - 余额调整与赠送用弹窗打开时生成的 ref（UUID）做幂等键：网络超时后再点一次提交，后端按 ref 去重，
 *     返回 created=false 时提示「已入账，没有重复记账」。
 *   - 策略整体 PUT（后端要求完整字段与原因），表单用当前策略预填，花瓣与微美元在提交时换算；
 *     对话正文一律保存（合规备查），策略里没有正文开关，只读展示全局保存天数。
 *   - 系统 Key（登录时自动创建，is_system）不显示吊销按钮，显示「系统 Key」标签与所属应用；
 *     其余 Key 客服也能吊销（泄露时要快）；建 Key、改策略、调余额只有 admin 能做。
 *   - 锁定会吊销该账号所有登录会话（后端完成）；运营账号只有 admin 能锁，自己不能改自己；
 *     运营角色只对 user 账号有意义，授予时要求对方已开两步验证，角色变化会吊销对方的管理会话。
 * 关键约束：调整金额单次最多 1,000 万花瓣（10,000 美元），与后端 MAX_ADJUST_UNITS 一致；
 *   按钮显隐只是体验，权限以后端为准（identity/routes_admin.py、gateway_control）。
 */
import { IconCoin, IconGift, IconKey, IconLock, IconLockOpen, IconSettings, IconShieldCog } from '@tabler/icons-react'
import { useEffect, useState } from 'react'
import { adminApi } from '@ap/api-client/admin'
import { newKey } from '@ap/api-client/http'
import { fmtInt, fmtMinor, fmtPetals, petals, petalsToUnits } from '@ap/api-client/money'
import type { Account, AccountOverview, AdminPolicy, ApiKey, CreatedKey, LedgerItem, OperatorRole, Order, UsageRecord } from '@ap/api-client/types'
import { A } from '@ap/i18n/admin'
import { C, errorText } from '@ap/i18n/common'
import { intlTag, useI18n } from '@ap/i18n'
import {
  Badge,
  Button,
  Card,
  ConfirmModal,
  CopyField,
  Field,
  Form,
  Input,
  Link,
  LoadMore,
  Loading,
  Modal,
  Notice,
  PageHead,
  Select,
  Stat,
  Switch,
  Table,
  Tabs,
  fmtDateTime,
  navigate,
  useLoad,
  usePaged,
  useToast,
  type Column,
} from '@ap/ui'
import { useAdmin } from '../session.tsx'
import { AuditList, ReasonModal, StatusBadge, identityAuditPage, useUsd } from '../shared.tsx'

type Tab = 'overview' | 'keys' | 'ledger' | 'usage' | 'orders' | 'audit'

export function AccountDetail({ id }: { id: string }) {
  const { t, locale } = useI18n()
  const toast = useToast()
  const { account: me, isAdmin } = useAdmin()
  const [tab, setTab] = useState<Tab>('overview')
  const acc = useLoad(() => adminApi.account(id), [id])
  const overview = useLoad(() => adminApi.accountOverview(id), [id])
  const [money, setMoney] = useState<'' | 'adjust' | 'bonus'>('')
  const [policyOpen, setPolicyOpen] = useState(false)
  const [locking, setLocking] = useState(false)
  const [roleOpen, setRoleOpen] = useState(false)
  const [role, setRole] = useState<'' | OperatorRole>('')

  if (acc.error) return <Notice tone="danger">{errorText(acc.error, locale)}</Notice>
  if (!acc.data) return <Loading />
  const a = acc.data
  const isService = a.kind === 'service'
  const self = me?.id === a.id
  const locked = a.status === 'locked'
  const canLock = !self && a.status !== 'deleted' && (isAdmin || !a.operator_role)

  return (
    <>
      <PageHead
        title={a.display_name || a.email || a.id}
        desc={
          <>
            <Badge tone={isService ? 'accent' : 'neutral'}>{t(isService ? A.accounts.kindService : A.accounts.kindUser)}</Badge>{' '}
            {a.email && <span className="ap-muted">{a.email} · </span>}
            <span className="ap-mono ap-muted">{a.id}</span>
          </>
        }
        actions={
          <>
            {canLock && (
              <Button
                variant={locked ? 'secondary' : 'danger'}
                icon={locked ? <IconLockOpen size={16} /> : <IconLock size={16} />}
                onClick={() => setLocking(true)}
              >
                {t(locked ? A.accounts.unlock : A.accounts.lock)}
              </Button>
            )}
            {isAdmin && !self && !isService && a.status === 'active' && (
              <Button
                icon={<IconShieldCog size={16} />}
                onClick={() => {
                  setRole(a.operator_role ?? '')
                  setRoleOpen(true)
                }}
              >
                {t(A.accounts.setRole)}
              </Button>
            )}
            {isAdmin && (
              <>
                <Button icon={<IconSettings size={16} />} disabled={!overview.data} onClick={() => setPolicyOpen(true)}>
                  {t(A.detail.editPolicy)}
                </Button>
                <Button icon={<IconGift size={16} />} onClick={() => setMoney('bonus')}>
                  {t(A.detail.grantBonus)}
                </Button>
                <Button variant="primary" icon={<IconCoin size={16} />} onClick={() => setMoney('adjust')}>
                  {t(A.detail.adjust)}
                </Button>
              </>
            )}
          </>
        }
      />
      {a.delete_requested_at && <Notice tone="warning">{t(A.accounts.deleteRequested, { date: fmtDateTime(a.delete_requested_at, locale) })}</Notice>}
      {isService && <Notice tone="accent">{t(A.accounts.serviceHint)}</Notice>}
      <Tabs
        items={[
          { id: 'overview', label: t(A.detail.tabOverview) },
          { id: 'keys', label: t(A.detail.tabKeys) },
          { id: 'ledger', label: t(A.detail.tabLedger) },
          { id: 'usage', label: t(A.detail.tabUsage) },
          { id: 'orders', label: t(A.detail.tabOrders) },
          { id: 'audit', label: t(A.detail.tabAudit) },
        ]}
        value={tab}
        onChange={setTab}
      />
      {tab === 'overview' && <OverviewTab account={a} ov={overview.data} error={overview.error} />}
      {tab === 'keys' && <KeysTab accountId={id} />}
      {tab === 'ledger' && <LedgerTab accountId={id} />}
      {tab === 'usage' && <UsageTab accountId={id} />}
      {tab === 'orders' && <OrdersTab accountId={id} />}
      {tab === 'audit' && (
        <div className="ap-stack">
          <Card flush title={t(A.nav.audit)}>
            <AuditList fetchPage={(c) => identityAuditPage((b) => adminApi.identityAudit(b, a.id), c)} deps={[a.id, acc.data]} />
          </Card>
          <Card flush title={t(A.nav.configAudit)}>
            <AuditList fetchPage={(c) => adminApi.controlAudit(c, { target: id })} deps={[id]} />
          </Card>
        </div>
      )}

      <MoneyModal
        kind={money}
        accountId={id}
        onClose={() => setMoney('')}
        onDone={() => {
          setMoney('')
          overview.reload()
        }}
      />
      {overview.data && (
        <PolicyModal
          open={policyOpen}
          accountId={id}
          policy={overview.data.policy}
          onClose={() => setPolicyOpen(false)}
          onDone={() => {
            setPolicyOpen(false)
            overview.reload()
          }}
        />
      )}
      <ReasonModal
        open={locking}
        danger={!locked}
        title={t(locked ? A.accounts.unlockTitle : A.accounts.lockTitle, { email: a.email || a.display_name || a.id })}
        lead={locked ? undefined : t(A.accounts.lockBody)}
        confirmLabel={t(locked ? A.accounts.unlock : A.accounts.lock)}
        onClose={() => setLocking(false)}
        onSubmit={async (reason) => {
          await adminApi.setAccountStatus(a.id, locked ? 'active' : 'locked', reason)
          setLocking(false)
          toast.ok(t(A.accounts.changed))
          acc.reload()
        }}
      />
      <ReasonModal
        open={roleOpen}
        title={t(A.accounts.setRole)}
        lead={t(A.accounts.roleBody)}
        onClose={() => setRoleOpen(false)}
        onSubmit={async (reason) => {
          await adminApi.setOperatorRole(a.id, role || null, reason)
          setRoleOpen(false)
          toast.ok(t(A.accounts.changed))
          acc.reload()
        }}
      >
        <Field label={t(A.accounts.operator)}>
          <Select
            value={role}
            onChange={(e) => setRole(e.target.value as '' | OperatorRole)}
            options={[
              { value: '', label: t(A.accounts.roleNone) },
              { value: 'support', label: t(A.role.support) },
              { value: 'admin', label: t(A.role.admin) },
            ]}
          />
        </Field>
      </ReasonModal>
    </>
  )
}

/** 概况：身份与状态、钱包三卡、策略。service 账号没有登录入口，隐藏运营角色、两步验证、邮箱验证与会话。 */
function OverviewTab({ account: a, ov, error }: { account: Account; ov: AccountOverview | null; error: unknown }) {
  const { t, locale } = useI18n()
  const tag = intlTag(locale)
  const isService = a.kind === 'service'
  return (
    <>
      <Card title={t(A.accounts.title)}>
        <dl className="ap-kv">
          <dt>ID</dt>
          <dd>
            <CopyField value={a.id} />
          </dd>
          <dt>{t(A.accounts.kind)}</dt>
          <dd>
            <Badge tone={isService ? 'accent' : 'neutral'}>{t(isService ? A.accounts.kindService : A.accounts.kindUser)}</Badge>
          </dd>
          <dt>{t(C.status)}</dt>
          <dd>
            <StatusBadge status={a.status} />
          </dd>
          {!isService && (
            <>
              <dt>{t(A.accounts.operator)}</dt>
              <dd>{a.operator_role ? t(A.role[a.operator_role]) : '-'}</dd>
              <dt>{t(A.accounts.totp)}</dt>
              <dd>{t(a.totp_enabled ? C.on : C.off)}</dd>
              <dt>{t(A.accounts.emailVerified)}</dt>
              <dd>{t(a.email_verified ? A.accounts.verified : A.accounts.unverified)}</dd>
              <dt>{t(C.language)}</dt>
              <dd>{a.locale}</dd>
              <dt>{t(A.accounts.sessions)}</dt>
              <dd>{a.active_sessions ?? '-'}</dd>
            </>
          )}
          <dt>{t(C.createdAt)}</dt>
          <dd>{fmtDateTime(a.created_at, locale)}</dd>
        </dl>
      </Card>
      {error ? (
        <Notice tone="danger">{errorText(error, locale)}</Notice>
      ) : !ov ? (
        <Loading />
      ) : (
        <WalletPolicy ov={ov} tag={tag} />
      )}
    </>
  )
}

function WalletPolicy({ ov, tag }: { ov: AccountOverview; tag: string }) {
  const { t } = useI18n()
  const w = ov.wallet
  const p = ov.policy
  return (
    <>
      {w.balance_units < 0 && <Notice tone="warning">{t(A.detail.negative, { n: fmtPetals(w.balance_units, tag) })}</Notice>}
      <div className="ap-grid3">
        <Card>
          <Stat
            label={t(A.detail.available)}
            value={fmtPetals(w.available_units, tag)}
            sub={`${t(A.detail.held, { n: fmtPetals(w.held_units, tag) })} · ${t(A.detail.unsettled, { n: fmtPetals(w.unsettled_units, tag) })}`}
          />
        </Card>
        <Card>
          <Stat label={t(A.detail.paid)} value={fmtPetals(w.paid_units, tag)} />
        </Card>
        <Card>
          <Stat label={t(A.detail.bonus)} value={fmtPetals(w.bonus_units, tag)} sub={`${t(A.detail.activeKeys)} ${ov.active_keys}`} />
        </Card>
      </div>
      <Card title={t(A.detail.policy)}>
        <dl className="ap-kv">
          <dt>{t(C.status)}</dt>
          <dd>
            <StatusBadge status={p.status} />
          </dd>
          <dt>{t(A.detail.rpm)}</dt>
          <dd>{p.rpm ?? t(C.unlimited)}</dd>
          <dt>{t(A.detail.concurrent)}</dt>
          <dd>{p.max_concurrent ?? t(C.unlimited)}</dd>
          <dt>{t(A.detail.daily)}</dt>
          <dd>{p.daily_spend_limit_units != null ? fmtPetals(p.daily_spend_limit_units, tag) : t(C.unlimited)}</dd>
          <dt>{t(A.detail.retainFixed)}</dt>
          <dd>{t(A.detail.retainFixedValue, { n: p.body_retention_days })}</dd>
          <dt>{t(A.detail.vendorExport)}</dt>
          <dd>{t(p.allow_vendor_export ? C.yes : C.no)}</dd>
        </dl>
      </Card>
    </>
  )
}

function MoneyModal({ kind, accountId, onClose, onDone }: { kind: '' | 'adjust' | 'bonus'; accountId: string; onClose: () => void; onDone: () => void }) {
  const { t } = useI18n()
  const toast = useToast()
  const [amount, setAmount] = useState('')
  const [ref, setRef] = useState('')
  const [lastKind, setLastKind] = useState<typeof kind>('')
  if (kind !== lastKind) {
    setLastKind(kind)
    if (kind) {
      setAmount('')
      setRef(newKey())
    }
  }
  return (
    <ReasonModal
      open={kind !== ''}
      title={t(kind === 'bonus' ? A.detail.grantBonus : A.detail.adjust)}
      lead={t(kind === 'bonus' ? A.detail.bonusHint : A.detail.adjustHint)}
      reasonLabel={t(A.detail.memo)}
      onClose={onClose}
      onSubmit={async (memo) => {
        const units = petalsToUnits(Number(amount))
        const r = kind === 'bonus' ? await adminApi.bonus(accountId, units, memo, ref) : await adminApi.adjust(accountId, units, memo, ref)
        toast.ok(t(r.created ? A.detail.posted : A.detail.duplicate))
        onDone()
      }}
    >
      <Field label={t(A.detail.amountPetals)}>
        <Input
          type="number"
          step="any"
          required
          min={kind === 'bonus' ? 0.001 : -10_000_000}
          max={10_000_000}
          autoFocus
          value={amount}
          onChange={(e) => setAmount(e.target.value)}
        />
      </Field>
    </ReasonModal>
  )
}

function PolicyModal({
  open,
  accountId,
  policy,
  onClose,
  onDone,
}: {
  open: boolean
  accountId: string
  policy: AdminPolicy
  onClose: () => void
  onDone: () => void
}) {
  const { t } = useI18n()
  const toast = useToast()
  const [status, setStatus] = useState(policy.status)
  const [rpm, setRpm] = useState('')
  const [conc, setConc] = useState('')
  const [daily, setDaily] = useState('')
  const [vendor, setVendor] = useState(false)
  const [wasOpen, setWasOpen] = useState(false)
  if (open !== wasOpen) {
    setWasOpen(open)
    if (open) {
      setStatus(policy.status)
      setRpm(policy.rpm != null ? String(policy.rpm) : '')
      setConc(policy.max_concurrent != null ? String(policy.max_concurrent) : '')
      setDaily(policy.daily_spend_limit_units != null ? String(petals(policy.daily_spend_limit_units)) : '')
      setVendor(policy.allow_vendor_export)
    }
  }
  const num = (v: string) => (v.trim() === '' ? null : Number(v))
  return (
    <ReasonModal
      open={open}
      title={t(A.detail.editPolicy)}
      onClose={onClose}
      onSubmit={async (reason) => {
        const d = num(daily)
        await adminApi.putPolicy(accountId, {
          status,
          rpm: num(rpm),
          max_concurrent: num(conc),
          daily_spend_limit: d == null ? null : petalsToUnits(d),
          allow_vendor_export: vendor,
          reason,
        })
        toast.ok(t(C.saved))
        onDone()
      }}
    >
      <Field label={t(C.status)}>
        <Select
          value={status}
          onChange={(e) => setStatus(e.target.value as AdminPolicy['status'])}
          options={[
            { value: 'active', label: t(A.status.active) },
            { value: 'suspended', label: t(A.status.suspended) },
          ]}
        />
      </Field>
      <div className="ap-grid2">
        <Field label={t(A.detail.rpm)} hint={t(A.detail.emptyMeansNone)}>
          <Input type="number" min={1} value={rpm} onChange={(e) => setRpm(e.target.value)} />
        </Field>
        <Field label={t(A.detail.concurrent)} hint={t(A.detail.emptyMeansNone)}>
          <Input type="number" min={1} value={conc} onChange={(e) => setConc(e.target.value)} />
        </Field>
      </div>
      <Field label={t(A.detail.daily)} hint={t(A.detail.emptyMeansNone)}>
        <Input type="number" min={0} step="any" value={daily} onChange={(e) => setDaily(e.target.value)} />
      </Field>
      <Switch checked={vendor} onChange={setVendor} label={t(A.detail.vendorExport)} />
    </ReasonModal>
  )
}

function keyState(k: ApiKey): 'revoked' | 'expired' | 'active' {
  if (k.revoked_at) return 'revoked'
  if (k.expires_at && new Date(k.expires_at).getTime() <= Date.now()) return 'expired'
  return 'active'
}

const KEY_STATE_TONE = { revoked: 'neutral', expired: 'warning', active: 'success' } as const

function KeysTab({ accountId }: { accountId: string }) {
  const { t, locale } = useI18n()
  const tag = intlTag(locale)
  const toast = useToast()
  const { isAdmin } = useAdmin()
  const [showRevoked, setShowRevoked] = useState(false)
  const keys = useLoad(() => adminApi.keys({ account_id: accountId, include_revoked: showRevoked }), [accountId, showRevoked])
  const [revoking, setRevoking] = useState<ApiKey | null>(null)
  const [busy, setBusy] = useState(false)
  const [creating, setCreating] = useState(false)
  const [created, setCreated] = useState<CreatedKey | null>(null)
  const [pricing, setPricing] = useState<ApiKey | null>(null)

  const revoke = async () => {
    if (!revoking) return
    setBusy(true)
    try {
      await adminApi.revokeKey(accountId, revoking.id)
      setRevoking(null)
      keys.reload()
    } catch (e) {
      toast.error(e)
    } finally {
      setBusy(false)
    }
  }

  const stateLabel = { revoked: A.detail.keyRevoked, expired: A.detail.keyExpired, active: A.detail.keyActive }
  const cols: Column<ApiKey>[] = [
    {
      key: 'n',
      title: t(C.name),
      render: (k) => (
        <div>
          <div className="ap-row" style={{ gap: 6 }}>
            <span style={{ fontWeight: 600 }}>{k.name}</span>
            {k.is_system && (
              <span title={t(A.detail.systemKeyHint)}>
                <Badge tone="accent">{t(A.detail.systemKey)}</Badge>
              </span>
            )}
          </div>
          <div className="ap-mono ap-muted">
            {k.display}
            {k.client_id ? ` · ${t(A.detail.systemKeyApp, { id: k.client_id })}` : ''}
          </div>
        </div>
      ),
    },
    { key: 'p', title: t(A.detail.purpose), render: (k) => k.purpose || <span className="ap-faint">-</span> },
    { key: 'lim', title: `${t(A.detail.rpm)} / ${t(A.detail.concurrent)}`, nowrap: true, render: (k) => `${fmtInt(k.rpm, tag)} / ${fmtInt(k.max_concurrent, tag)}` },
    { key: 'pri', title: t(A.detail.priority), render: (k) => <Badge>{k.max_priority}</Badge> },
    {
      key: 'bill',
      title: t(A.detail.billing),
      nowrap: true,
      render: (k) =>
        k.billing_mode === 'metered' ? (
          <Badge tone="warning">{t(A.detail.metered)}</Badge>
        ) : (
          <span className="ap-mono">
            x{k.price_multiplier}
            {k.discount_code ? ` · ${k.discount_code}` : ''}
          </span>
        ),
    },
    {
      key: 'q',
      title: `${t(A.detail.used)} / ${t(A.detail.quota)}`,
      num: true,
      nowrap: true,
      render: (k) =>
        k.quota_limit_units != null
          ? `${fmtPetals(k.quota_used_units ?? 0, tag)} / ${fmtPetals(k.quota_limit_units, tag)}`
          : fmtPetals(k.quota_used_units ?? 0, tag),
    },
    { key: 'last', title: t(A.detail.lastUsed), nowrap: true, muted: true, render: (k) => fmtDateTime(k.last_used_at, locale) || t(C.never) },
    {
      key: 's',
      title: t(C.status),
      render: (k) => {
        const s = keyState(k)
        return <Badge tone={KEY_STATE_TONE[s]}>{t(stateLabel[s])}</Badge>
      },
    },
    {
      key: 'a',
      title: '',
      render: (k) =>
        k.revoked_at ? null : (
          <div className="ap-row" style={{ gap: 4 }}>
            {isAdmin && (
              <Button size="sm" variant="ghost" onClick={() => setPricing(k)}>
                {t(A.detail.editPricing)}
              </Button>
            )}
            {!k.is_system && (
              <Button size="sm" variant="ghost" onClick={() => setRevoking(k)}>
                {t(A.detail.revoke)}
              </Button>
            )}
          </div>
        ),
    },
  ]

  return (
    <>
      <div className="ap-row ap-row--between">
        <Switch checked={showRevoked} onChange={setShowRevoked} label={t(A.detail.showRevoked)} />
        {isAdmin && (
          <Button variant="primary" icon={<IconKey size={16} />} onClick={() => setCreating(true)}>
            {t(A.detail.createKey)}
          </Button>
        )}
      </div>
      <Card flush>
        {keys.error ? (
          <div className="ap-card__body">
            <Notice tone="danger">{errorText(keys.error, locale)}</Notice>
          </div>
        ) : (
          <Table columns={cols} rows={keys.data?.data} loading={keys.loading} rowKey={(k) => k.id} />
        )}
      </Card>
      <ConfirmModal
        open={revoking !== null}
        danger
        busy={busy}
        title={t(A.detail.revokeTitle, { name: revoking?.name ?? '' })}
        confirmLabel={t(A.detail.revoke)}
        onConfirm={revoke}
        onClose={() => setRevoking(null)}
      >
        <div className="ap-stack" style={{ gap: 8 }}>
          <span className="ap-mono">{revoking?.display}</span>
          <span>{t(A.detail.revokeBody)}</span>
        </div>
      </ConfirmModal>
      <KeyPricingModal
        accountId={accountId}
        keyItem={pricing}
        onClose={() => setPricing(null)}
        onSaved={() => {
          setPricing(null)
          keys.reload()
        }}
      />
      <ServiceKeyForm
        open={creating}
        accountId={accountId}
        onClose={() => setCreating(false)}
        onCreated={(k) => {
          setCreating(false)
          setCreated(k)
          keys.reload()
        }}
      />
      <Modal
        open={created !== null}
        onClose={() => setCreated(null)}
        title={created?.name ?? ''}
        footer={
          <Button variant="primary" onClick={() => setCreated(null)}>
            {t(C.close)}
          </Button>
        }
      >
        {created && (
          <div className="ap-stack" style={{ gap: 12 }}>
            <Notice tone="warning">{t(A.detail.createdOnce)}</Notice>
            <CopyField value={created.key} />
          </div>
        )}
      </Modal>
    </>
  )
}

/** 管理员改单个 Key 的价格倍率与优惠码；倍率 0 表示只记账不扣费。接口只允许管理员改这两项。 */
function KeyPricingModal({
  accountId,
  keyItem,
  onClose,
  onSaved,
}: {
  accountId: string
  keyItem: ApiKey | null
  onClose: () => void
  onSaved: () => void
}) {
  const { t, locale } = useI18n()
  const toast = useToast()
  const [mult, setMult] = useState('1')
  const [code, setCode] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')

  useEffect(() => {
    if (keyItem) {
      setMult(keyItem.price_multiplier)
      setCode(keyItem.discount_code)
      setError('')
    }
  }, [keyItem])

  const submit = async () => {
    if (!keyItem) return
    setBusy(true)
    setError('')
    try {
      await adminApi.updateKey(accountId, keyItem.id, { price_multiplier: mult.trim(), discount_code: code.trim() })
      toast.ok(t(A.detail.pricingSaved))
      onSaved()
    } catch (e) {
      setError(errorText(e, locale))
    } finally {
      setBusy(false)
    }
  }

  return (
    <Modal open={keyItem !== null} onClose={onClose} busy={busy} title={`${t(A.detail.editPricing)} · ${keyItem?.name ?? ''}`}>
      <Form onSubmit={submit}>
        {error && <Notice tone="danger">{error}</Notice>}
        <Field label={t(A.detail.priceMultiplier)} hint={t(A.detail.priceMultiplierHint)}>
          <Input required inputMode="decimal" pattern="[01](\.[0-9]{1,8})?" value={mult} onChange={(e) => setMult(e.target.value)} />
        </Field>
        <Field label={t(A.detail.discountCode)} hint={t(A.detail.discountCodeHint)}>
          <Input maxLength={64} value={code} onChange={(e) => setCode(e.target.value)} />
        </Field>
        <div className="ap-row" style={{ justifyContent: 'flex-end' }}>
          <Button onClick={onClose} disabled={busy}>
            {t(C.cancel)}
          </Button>
          <Button type="submit" variant="primary" loading={busy}>
            {t(C.save)}
          </Button>
        </div>
      </Form>
    </Modal>
  )
}

function ServiceKeyForm({
  open,
  accountId,
  onClose,
  onCreated,
}: {
  open: boolean
  accountId: string
  onClose: () => void
  onCreated: (k: CreatedKey) => void
}) {
  const { t, locale } = useI18n()
  const [name, setName] = useState('')
  const [purpose, setPurpose] = useState('')
  const [priority, setPriority] = useState('p1')
  const [rpm, setRpm] = useState('600')
  const [conc, setConc] = useState('50')
  const [quota, setQuota] = useState('')
  const [models, setModels] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')

  const submit = async () => {
    setBusy(true)
    setError('')
    try {
      const list = models
        .split(',')
        .map((s) => s.trim())
        .filter(Boolean)
      const k = await adminApi.createKey(accountId, {
        name: name.trim(),
        purpose: purpose.trim(),
        max_priority: priority,
        rpm: Number(rpm),
        max_concurrent: Number(conc),
        ...(quota.trim() ? { quota_limit: petalsToUnits(Number(quota)) } : {}),
        ...(list.length ? { allowed_models: list } : {}),
      })
      setName('')
      setPurpose('')
      setQuota('')
      setModels('')
      onCreated(k)
    } catch (e) {
      setError(errorText(e, locale))
    } finally {
      setBusy(false)
    }
  }

  return (
    <Modal open={open} onClose={onClose} busy={busy} title={t(A.detail.createKey)}>
      <Form onSubmit={submit}>
        {error && <Notice tone="danger">{error}</Notice>}
        <div className="ap-grid2">
          <Field label={t(C.name)}>
            <Input required maxLength={80} autoFocus value={name} onChange={(e) => setName(e.target.value)} />
          </Field>
          <Field label={t(A.detail.purpose)} hint={t(A.detail.purposeHint)}>
            <Input mono pattern="[a-z0-9\-]{0,40}" value={purpose} onChange={(e) => setPurpose(e.target.value)} />
          </Field>
          <Field label={t(A.detail.priority)}>
            <Select
              value={priority}
              onChange={(e) => setPriority(e.target.value)}
              options={['p0', 'p1', 'p2', 'p3'].map((p) => ({ value: p, label: p }))}
            />
          </Field>
          <Field label={t(A.detail.quota)} hint={t(A.detail.emptyMeansNone)}>
            <Input type="number" min={0} step="any" value={quota} onChange={(e) => setQuota(e.target.value)} />
          </Field>
          <Field label={t(A.detail.rpm)}>
            <Input type="number" min={1} max={100000} required value={rpm} onChange={(e) => setRpm(e.target.value)} />
          </Field>
          <Field label={t(A.detail.concurrent)}>
            <Input type="number" min={1} max={10000} required value={conc} onChange={(e) => setConc(e.target.value)} />
          </Field>
        </div>
        <Field label={t(A.detail.models)} hint={t(A.detail.emptyMeansNone)}>
          <Input mono value={models} onChange={(e) => setModels(e.target.value)} />
        </Field>
        <div className="ap-row" style={{ justifyContent: 'flex-end' }}>
          <Button onClick={onClose} disabled={busy}>
            {t(C.cancel)}
          </Button>
          <Button type="submit" variant="primary" loading={busy}>
            {t(C.create)}
          </Button>
        </div>
      </Form>
    </Modal>
  )
}

function LedgerTab({ accountId }: { accountId: string }) {
  const { t, locale } = useI18n()
  const tag = intlTag(locale)
  const list = usePaged<LedgerItem>((c) => adminApi.accountLedger(accountId, c), [accountId])
  const cols: Column<LedgerItem>[] = [
    { key: 't', title: t(C.time), nowrap: true, muted: true, render: (x) => fmtDateTime(x.created_at, locale) },
    { key: 'k', title: t(A.detail.kind), render: (x) => <Badge>{C.ledgerKind[x.kind] ? t(C.ledgerKind[x.kind]) : x.kind}</Badge> },
    {
      key: 'd',
      title: t(C.petals),
      num: true,
      render: (x) => {
        const total = x.paid_delta_units + x.bonus_delta_units
        return <span className={total < 0 ? 'ap-danger-text' : 'ap-success-text'}>{(total > 0 ? '+' : '') + fmtPetals(total, tag)}</span>
      },
    },
    { key: 'm', title: t(A.detail.memo), render: (x) => x.memo || <span className="ap-faint">-</span> },
    { key: 'a', title: t(A.audit.actor), muted: true, render: (x) => <span className="ap-mono">{x.actor}</span> },
    { key: 'r', title: 'ref', muted: true, render: (x) => <span className="ap-mono">{x.ref}</span> },
  ]
  return (
    <Card flush>
      {list.error ? (
        <div className="ap-card__body">
          <Notice tone="danger">{errorText(list.error, locale)}</Notice>
        </div>
      ) : (
        <Table columns={cols} rows={list.items} loading={list.loading} rowKey={(x) => x.id} />
      )}
      <LoadMore hasMore={list.hasMore} loading={list.loading} onClick={list.loadMore} />
    </Card>
  )
}

function UsageTab({ accountId }: { accountId: string }) {
  const { t, locale } = useI18n()
  const tag = intlTag(locale)
  const usd = useUsd()
  const list = usePaged<UsageRecord>((c) => adminApi.accountUsageRecords(accountId, c), [accountId])
  const cols: Column<UsageRecord>[] = [
    { key: 't', title: t(C.time), nowrap: true, muted: true, render: (r) => fmtDateTime(r.created_at, locale) },
    { key: 'm', title: t(C.model), render: (r) => <span className="ap-mono">{r.model}</span> },
    {
      key: 'u',
      title: t(A.detail.upstream),
      render: (r) => (
        <span className="ap-mono ap-muted">
          {r.upstream_model || '-'} · {r.channel_id || '-'}
          {r.attempts && r.attempts > 1 ? ` ×${r.attempts}` : ''}
        </span>
      ),
    },
    {
      key: 'tok',
      title: `${t(C.inputTokens)} / ${t(C.outputTokens)}`,
      num: true,
      nowrap: true,
      render: (r) => `${fmtInt(r.input_tokens, tag)} / ${fmtInt(r.output_tokens, tag)}`,
    },
    { key: 'c', title: t(C.cost), num: true, render: (r) => fmtPetals(r.cost, tag) },
    { key: 'uc', title: t(A.dash.upstream), num: true, muted: true, render: (r) => usd(r.upstream_cost, 4) },
    {
      key: 's',
      title: t(C.status),
      render: (r) => (r.status === 'ok' ? <Badge tone="success">ok</Badge> : <Badge tone="danger">{r.error_code || r.status}</Badge>),
    },
    { key: 'src', title: t(A.detail.purpose), muted: true, render: (r) => [r.purpose, r.source, r.end_user].filter(Boolean).join(' · ') || '-' },
  ]
  return (
    <Card flush>
      {list.error ? (
        <div className="ap-card__body">
          <Notice tone="danger">{errorText(list.error, locale)}</Notice>
        </div>
      ) : (
        <Table columns={cols} rows={list.items} loading={list.loading} rowKey={(r) => r.request_id} />
      )}
      <LoadMore hasMore={list.hasMore} loading={list.loading} onClick={list.loadMore} />
    </Card>
  )
}

/** 订单列表：账号详情按 accountId 过滤，订单页还可以按状态过滤。 */
export function OrdersTable({ accountId, status }: { accountId?: string; status?: string }) {
  const { t, locale } = useI18n()
  const tag = intlTag(locale)
  const list = usePaged<Order>((c) => adminApi.orders({ account_id: accountId || undefined, status: status || undefined, cursor: c }), [accountId, status])
  const cols: Column<Order>[] = [
    { key: 't', title: t(C.time), nowrap: true, muted: true, render: (o) => fmtDateTime(o.created_at, locale) },
    { key: 'id', title: t(A.pay.order), render: (o) => <span className="ap-mono">{o.id.slice(0, 8)}</span> },
    ...(accountId
      ? []
      : [{ key: 'account', title: t(A.pay.account), render: (o: Order) => <Link href={`/accounts/${o.account_id}`} className="ap-mono" onClick={(e) => e.stopPropagation()}>{o.account_id.slice(0, 8)}</Link> }]),
    { key: 'sku', title: t(A.pay.sku), render: (o) => o.sku },
    { key: 'amt', title: t(A.pay.amount), num: true, render: (o) => fmtMinor(o.amount_minor, o.currency, tag) },
    { key: 'p', title: t(A.pay.petals), num: true, render: (o) => fmtInt(o.petals, tag) },
    {
      key: 's',
      title: t(C.status),
      render: (o) => <Badge tone={o.status === 'fulfilled' ? 'success' : o.status === 'refunded' || o.status === 'revoked' ? 'warning' : 'neutral'}>{t(C.orderStatus[o.status])}</Badge>,
    },
  ]
  return (
    <>
      {list.error ? (
        <div className="ap-card__body">
          <Notice tone="danger">{errorText(list.error, locale)}</Notice>
        </div>
      ) : (
        <Table columns={cols} rows={list.items} loading={list.loading} rowKey={(o) => o.id} onRowClick={(o) => navigate(`/payments/orders/${o.id}`)} />
      )}
      <LoadMore hasMore={list.hasMore} loading={list.loading} onClick={list.loadMore} />
    </>
  )
}

function OrdersTab({ accountId }: { accountId: string }) {
  return (
    <Card flush>
      <OrdersTable accountId={accountId} />
    </Card>
  )
}
